/**
 * Xtream Codes panel surface: response shapes, URL construction, and the
 * coercions that keep real-world panels from breaking the client.
 *
 * Panels are wildly inconsistent. `stream_id` arrives as 1234 or "1234",
 * `category_id` as "5" or 5 or null, `rating` as "7.4" or 7.4 or "". Every
 * field is therefore read through a coercion helper rather than trusted.
 */

/**
 * Several panels challenge generic HTTP clients (empty body, 403, or an HTML
 * block page) while allowing established IPTV players through. Matching what
 * desktop players send is the difference between a working and a "broken"
 * account, so API calls, probes and stream fetches must all use this.
 */
export const XTREAM_CLIENT_USER_AGENT = 'VLC/3.0.18 LibVLC/3.0.18';

export const XTREAM_ACTIONS = {
  liveCategories: 'get_live_categories',
  liveStreams: 'get_live_streams',
  vodCategories: 'get_vod_categories',
  vodStreams: 'get_vod_streams',
  vodInfo: 'get_vod_info',
  seriesCategories: 'get_series_categories',
  series: 'get_series',
  seriesInfo: 'get_series_info',
  shortEpg: 'get_short_epg',
} as const;

export type XtreamAction = (typeof XTREAM_ACTIONS)[keyof typeof XTREAM_ACTIONS];

// ---------------------------------------------------------------------------
// Coercion
// ---------------------------------------------------------------------------

/** Panels send numbers as strings and vice versa. Returns null for junk. */
export function asNumber(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

export function asString(value: unknown): string | null {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed ? trimmed : null;
  }
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return null;
}

/** `auth`/`tv_archive` style flags arrive as 1/0/"1"/"0"/true/false. */
export function asBool(value: unknown): boolean {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  if (typeof value === 'string') {
    const v = value.trim().toLowerCase();
    return v === '1' || v === 'true' || v === 'yes';
  }
  return false;
}

/** Epoch seconds from `added` / `exp_date`, which are usually numeric strings. */
export function asEpochSeconds(value: unknown): number | null {
  const n = asNumber(value);
  if (n === null) return null;
  // Some panels send milliseconds. Anything past year 5138 in seconds is ms.
  if (n > 100_000_000_000) return Math.floor(n / 1000);
  return Math.floor(n);
}

/**
 * `get_*_streams` returns `[]` for an empty category, but some panels return
 * `false`, `null`, or an object keyed by index instead. Normalize all of it.
 */
export function asArray<T>(value: unknown): T[] {
  if (Array.isArray(value)) return value as T[];
  if (value && typeof value === 'object') {
    const values = Object.values(value as Record<string, unknown>);
    // Only treat it as a list if every member really looks like a row.
    if (values.length && values.every((entry) => entry && typeof entry === 'object')) return values as T[];
  }
  return [];
}

// ---------------------------------------------------------------------------
// Response shapes (deliberately loose — coerce before use)
// ---------------------------------------------------------------------------

export interface XtreamUserInfo {
  username?: string;
  password?: string;
  message?: string;
  auth?: number | boolean | string;
  status?: string;
  exp_date?: string | number | null;
  is_trial?: string | number;
  active_cons?: string | number;
  created_at?: string | number;
  max_connections?: string | number;
  allowed_output_formats?: string[];
}

export interface XtreamServerInfo {
  url?: string;
  port?: string | number;
  https_port?: string | number;
  server_protocol?: string;
  rtmp_port?: string | number;
  timezone?: string;
  timestamp_now?: number;
  time_now?: string;
}

export interface XtreamAuthResponse {
  user_info?: XtreamUserInfo;
  server_info?: XtreamServerInfo;
}

export interface XtreamCategoryRaw {
  category_id?: string | number;
  category_name?: string;
  parent_id?: string | number;
}

export interface XtreamStreamRaw {
  num?: number;
  name?: string;
  title?: string;
  stream_type?: string;
  stream_id?: string | number;
  series_id?: string | number;
  stream_icon?: string;
  cover?: string;
  epg_channel_id?: string | null;
  added?: string | number;
  category_id?: string | number | null;
  category_ids?: (string | number)[];
  custom_sid?: string | null;
  tv_archive?: string | number;
  tv_archive_duration?: string | number;
  direct_source?: string;
  container_extension?: string;
  rating?: string | number;
  rating_5based?: string | number;
  releaseDate?: string;
  release_date?: string;
  year?: string | number;
  plot?: string;
  cast?: string;
  director?: string;
  genre?: string;
  backdrop_path?: string[];
  youtube_trailer?: string;
  episode_run_time?: string | number;
  last_modified?: string | number;
}

export interface XtreamEpisodeRaw {
  id?: string | number;
  episode_num?: string | number;
  title?: string;
  container_extension?: string;
  season?: string | number;
  added?: string | number;
  info?: {
    duration_secs?: string | number;
    duration?: string;
    movie_image?: string;
    plot?: string;
    rating?: string | number;
    releasedate?: string;
    name?: string;
  };
}

export interface XtreamSeriesInfoRaw {
  seasons?: unknown[];
  info?: XtreamStreamRaw;
  /** Keyed by season number as a string. */
  episodes?: Record<string, XtreamEpisodeRaw[]>;
}

export interface XtreamVodInfoRaw {
  info?: Record<string, unknown>;
  movie_data?: XtreamStreamRaw;
}

// ---------------------------------------------------------------------------
// URL construction
// ---------------------------------------------------------------------------

/** `player_api.php`, `panel_api.php` and `get.php` all identify a panel root. */
const API_ENDPOINT_SUFFIX = /\/(?:get|player_api|panel_api|portal)\.php$/i;

export class XtreamUrlError extends Error {}

/**
 * Reduce anything a user might paste to `origin + basePath` with no trailing
 * slash. Accepts a bare host, a full `player_api.php` URL, or a URL carrying
 * `?username=&password=`.
 */
export function normalizeServerUrl(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) throw new XtreamUrlError('Server URL is required');

  const withScheme = /^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed) ? trimmed : `http://${trimmed}`;

  let url: URL;
  try {
    url = new URL(withScheme);
  } catch {
    throw new XtreamUrlError('Server URL is not a valid URL');
  }

  if (url.protocol !== 'http:' && url.protocol !== 'https:') {
    throw new XtreamUrlError('Only http:// and https:// panels are supported');
  }
  if (url.username || url.password) {
    throw new XtreamUrlError('Put the username and password in their own fields, not in the URL');
  }

  const path = url.pathname.replace(/\/+$/, '').replace(API_ENDPOINT_SUFFIX, '');
  return `${url.origin}${path}`;
}

/** Pull `username`/`password` out of a pasted `player_api.php?...` URL. */
export function extractCredentialsFromUrl(value: string): { username: string; password: string } | null {
  let url: URL;
  try {
    url = new URL(value.trim());
  } catch {
    return null;
  }
  const username = url.searchParams.get('username')?.trim() ?? '';
  const password = url.searchParams.get('password')?.trim() ?? '';
  return username && password ? { username, password } : null;
}

const MAX_PLAYLIST_URL_CHARS = 2000;

/**
 * Check a public playlist address and return it in a canonical form.
 *
 * Unlike a panel address this keeps the whole path and query, because the file
 * itself is what gets fetched. It must be an `http(s)` address with no login in
 * it: the address is shown to everyone who can watch the playlist, so a
 * username or password embedded in it would be shown too. A protected line
 * belongs in the server URL, username and password fields instead.
 */
export function normalizePlaylistUrl(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) throw new XtreamUrlError('Enter a playlist URL');
  if (trimmed.length > MAX_PLAYLIST_URL_CHARS) throw new XtreamUrlError('The playlist URL is too long');
  if (!/^https?:\/\//i.test(trimmed)) throw new XtreamUrlError('The playlist URL must start with http:// or https://');

  let url: URL;
  try {
    url = new URL(trimmed);
  } catch {
    throw new XtreamUrlError('The playlist URL is not a valid URL');
  }
  if (!url.hostname) throw new XtreamUrlError('The playlist URL is not a valid URL');
  if (url.username || url.password || extractCredentialsFromUrl(trimmed)) {
    throw new XtreamUrlError(
      'A public playlist URL cannot contain a username or password. For a protected line, enter the server URL with a username and password.',
    );
  }

  url.hash = '';
  return url.href;
}

export interface XtreamCredentials {
  serverUrl: string;
  username: string;
  password: string;
}

/** Build a `player_api.php` URL. Omit `action` for the bare auth probe. */
export function buildApiUrl(
  creds: XtreamCredentials,
  action?: XtreamAction,
  params?: Record<string, string | number | undefined>,
): string {
  const url = new URL(apiEndpoint(creds));
  for (const [key, value] of apiFields(creds, action, params)) url.searchParams.set(key, value);
  return url.toString();
}

/** `player_api.php` on this panel, with nothing in the query. */
export function apiEndpoint(creds: XtreamCredentials): string {
  return `${normalizeServerUrl(creds.serverUrl)}/player_api.php`;
}

/**
 * The same call as a form body.
 *
 * Xtream reads `$_REQUEST`, so a panel accepts its parameters in the query
 * string or in a POST body and cannot tell the two apart. The body is
 * preferred for one reason that always holds: a URL is the one place
 * credentials leak by default — logs, traces, `Referer`, caches — which is why
 * the API Worker also runs with `redact_query_string` on for the fallback.
 *
 * It is NOT preferred because any particular panel refuses the query form. An
 * earlier note here claimed playzix.xyz did; that was measured with the
 * throwaway pair `probe/probe`, and the panel turns out to answer 503 to any
 * GET where the username and the password are the same string. Real accounts
 * are unaffected, so the claim was wrong and is recorded here so it is not
 * rediscovered as fact.
 */
export function buildApiForm(
  creds: XtreamCredentials,
  action?: XtreamAction,
  params?: Record<string, string | number | undefined>,
): URLSearchParams {
  return new URLSearchParams(apiFields(creds, action, params));
}

function apiFields(
  creds: XtreamCredentials,
  action?: XtreamAction,
  params?: Record<string, string | number | undefined>,
): [string, string][] {
  const fields: [string, string][] = [
    ['username', creds.username],
    ['password', creds.password],
  ];
  if (action) fields.push(['action', action]);
  for (const [key, value] of Object.entries(params ?? {})) {
    if (value !== undefined && value !== null && value !== '') fields.push([key, String(value)]);
  }
  return fields;
}

/**
 * Stream paths embed the credentials as *path segments*, so they must be
 * percent-encoded there — a password containing `/` or `#` silently produces a
 * 404 otherwise.
 */
function encodeSegment(value: string): string {
  return encodeURIComponent(value.trim());
}

export function buildLiveUrl(creds: XtreamCredentials, streamId: number, format: 'm3u8' | 'ts' = 'm3u8'): string {
  const base = normalizeServerUrl(creds.serverUrl);
  return `${base}/live/${encodeSegment(creds.username)}/${encodeSegment(creds.password)}/${Math.trunc(streamId)}.${format}`;
}

/**
 * A container extension is a short alphanumeric word. Anything else is refused
 * rather than spliced into the path: these values originate off-host, and the
 * builders interpolate them directly.
 */
function safeExtension(container: string): string {
  const ext = container.replace(/^\./, '').trim();
  return /^[a-z0-9]{1,8}$/i.test(ext) ? ext : 'mp4';
}

export function buildMovieUrl(creds: XtreamCredentials, streamId: number, container: string): string {
  const base = normalizeServerUrl(creds.serverUrl);
  return `${base}/movie/${encodeSegment(creds.username)}/${encodeSegment(creds.password)}/${Math.trunc(streamId)}.${safeExtension(container)}`;
}

export function buildEpisodeUrl(creds: XtreamCredentials, episodeId: string | number, container: string): string {
  const base = normalizeServerUrl(creds.serverUrl);
  return `${base}/series/${encodeSegment(creds.username)}/${encodeSegment(creds.password)}/${encodeSegment(String(episodeId))}.${safeExtension(container)}`;
}

// ---------------------------------------------------------------------------
// Account interpretation
// ---------------------------------------------------------------------------

export type PanelStatus = 'active' | 'expired' | 'banned' | 'unknown';

export interface PanelAccount {
  status: PanelStatus;
  message: string | null;
  expiresAt: number | null;
  maxConnections: number | null;
  activeConnections: number | null;
  isTrial: boolean;
  allowedFormats: string[] | null;
  timezone: string | null;
}

/**
 * What a panel says about an account, with anything URL-shaped taken out.
 *
 * The message is worth showing — "expired on 2026-03-01", "too many
 * connections" — but panels routinely put a renewal link in it, and that link
 * is routinely the `player_api.php?username=…&password=…` they were just
 * called with. It is rendered in the UI and lands in the browser's network log,
 * so the credentials cannot be allowed to ride along.
 */
export function sanitizePanelMessage(message: string | null): string | null {
  if (!message) return null;
  const cleaned = message
    // Any absolute URL, and any bare host:port/path that reads like one.
    .replace(/\b(?:[a-z][a-z0-9+.-]*:\/\/|www\.)\S+/gi, '[link removed]')
    .replace(/\b[\w.-]+\.[a-z]{2,}(?::\d+)?\/\S*/gi, '[link removed]')
    // And any stray credential pair, whatever carried it.
    .replace(/\b(user(?:name)?|pass(?:word)?|pwd)\s*[=:]\s*\S+/gi, '$1=[removed]')
    .trim();

  // A message that was ONLY a link is not worth showing as "[link removed]".
  const substance = cleaned.replace(/\[(?:link )?removed\]/gi, '').replace(/[^\p{L}\p{N}]+/gu, '');
  return substance ? cleaned : null;
}

export function interpretAuthResponse(response: XtreamAuthResponse | null | undefined, nowSeconds: number): PanelAccount {
  const info = response?.user_info;
  const server = response?.server_info;

  const allowedFormats = Array.isArray(info?.allowed_output_formats)
    ? info.allowed_output_formats.map((f) => String(f).trim().toLowerCase()).filter(Boolean)
    : null;

  const expiresAt = asEpochSeconds(info?.exp_date ?? null);
  const rawStatus = (info?.status ?? '').toString().trim().toLowerCase();

  let status: PanelStatus = 'unknown';
  if (info) {
    // `auth` is the panel's own verdict and outranks everything else.
    const authed = info.auth === undefined ? true : asBool(info.auth);
    if (!authed) status = 'banned';
    else if (rawStatus === 'banned' || rawStatus === 'disabled') status = 'banned';
    else if (rawStatus === 'expired') status = 'expired';
    else if (expiresAt !== null && expiresAt > 0 && expiresAt <= nowSeconds) status = 'expired';
    else if (rawStatus === 'active' || rawStatus === '') status = 'active';
    else status = 'active';
  }

  return {
    status,
    message: sanitizePanelMessage(asString(info?.message)),
    expiresAt,
    maxConnections: asNumber(info?.max_connections),
    activeConnections: asNumber(info?.active_cons),
    isTrial: asBool(info?.is_trial),
    allowedFormats: allowedFormats && allowedFormats.length ? allowedFormats : null,
    timezone: asString(server?.timezone),
  };
}

/**
 * Live format choice.
 *
 * MPEG-TS is preferred: one HTTP socket, which is what VLC/mpv/IPTVnator play
 * and what mpegts.js can play in a browser. HLS is the fallback for panels
 * that do not list `ts` in allowed_output_formats.
 */
export function resolveLiveFormat(allowedFormats: string[] | null | undefined, preferred: 'hls' | 'ts'): 'm3u8' | 'ts' {
  const allowed = allowedFormats?.map((f) => f.toLowerCase()).filter(Boolean) ?? [];
  const wanted = preferred === 'ts' ? 'ts' : 'm3u8';

  if (!allowed.length) return wanted;
  if (allowed.includes(wanted)) return wanted;
  if (allowed.includes('m3u8')) return 'm3u8';
  if (allowed.includes('ts')) return 'ts';
  return wanted;
}
